import React from "react";
import Footer from "../FooterBn";

/**
 * Goat Teachers — Terms & Conditions
 * Tailwind-only (no CSS), matching the same UI style as your Guidelines/About pages.
 */

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
    {children}
  </span>
);

const Card = ({ children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
    {children}
  </div>
);

const Section = ({ id, title, subtitle, children }) => (
  <section id={id} className="scroll-mt-24">
    <div className="mb-3">
      <h2 className="text-lg font-black text-slate-900 sm:text-xl">{title}</h2>
      {subtitle ? (
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          {subtitle}
        </p>
      ) : null}
    </div>
    <Card>{children}</Card>
  </section>
);

const RuleList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-700">
        <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-slate-300" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const InfoBox = ({ title, children, tone = "neutral" }) => {
  const tones = {
    neutral: "border-slate-200 bg-slate-50 text-slate-700",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    danger: "border-rose-200 bg-rose-50 text-rose-900",
  };
  return (
    <div
      className={`rounded-2xl border p-4 text-sm leading-relaxed ${tones[tone]}`}
    >
      <div className="mb-1 font-extrabold">{title}</div>
      <div>{children}</div>
    </div>
  );
};

const TermsAndConditions = () => {
  const effectiveDate = "20-12-2025";
  const lastUpdated = "20-12-2025";

  const navItems = [
    ["definitions", "1. Definitions"],
    ["eligibility", "2. Eligibility"],
    ["overview", "3. Platform Overview"],
    ["accounts", "4. User Accounts"],
    ["tutor", "5. Tutor Responsibilities"],
    ["student", "6. Student Responsibilities"],
    ["credits", "7. Credits, Payments, and Refund Policy"],
    ["content", "8. Reviews, Ratings, and User Content"],
    ["ip", "9. Intellectual Property"],
    ["privacy", "10. Privacy"],
    ["disclaimers", "11. Disclaimers"],
    ["liability", "12. Limitation of Liability"],
    ["indemnification", "13. Indemnification"],
    ["termination", "14. Termination"],
    ["modifications", "15. Modifications to Terms"],
    ["law", "16. Governing Law and Jurisdiction"],
    ["severability", "17. Severability"],
    ["contact", "18. Contact Information"],
  ];

  return (
    <>
      <div className="relative isolate bg-slate-50">
        {/* subtle background blobs */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-[-120px] h-[320px] w-[320px] -translate-x-1/2 rounded-full bg-slate-200/50 blur-3xl" />
          <div className="absolute right-[-120px] top-[180px] h-[320px] w-[320px] rounded-full bg-slate-200/40 blur-3xl" />
          <div className="absolute left-[-120px] top-[420px] h-[320px] w-[320px] rounded-full bg-slate-200/40 blur-3xl" />
        </div>

        <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
          {/* Header */}
          <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="mb-2 flex flex-wrap gap-2">
                  <Chip>Legal</Chip>
                  <Chip>Terms</Chip>
                  <Chip>Bangladesh</Chip>
                </div>

                <h1 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                  Goat Teachers – Terms &amp; Conditions
                </h1>

                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                  Welcome to Goat Teachers. These Terms and Conditions (“Terms”)
                  govern your access to and use of the Goat Teachers website,
                  mobile applications, services, networks, and any related
                  features (collectively, the “Platform”).
                  <br />
                  By accessing or using Goat Teachers, you agree to be legally
                  bound by these Terms. If you do not agree, you must not access
                  or use the Platform.
                </p>

                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                    Effective Date:{" "}
                    <span className="font-bold text-slate-900">
                      {effectiveDate}
                    </span>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                    Last Updated:{" "}
                    <span className="font-bold text-slate-900">
                      {lastUpdated}
                    </span>
                  </span>
                </div>
              </div>

              {/* Quick Nav */}
              <div className="w-full lg:max-w-xs">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Quick Navigation
                  </div>
                  <div className="mt-3 grid gap-2 text-sm">
                    {navItems.map(([id, label]) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-700 shadow-sm hover:border-slate-300"
                      >
                        <span className="font-semibold">{label}</span>
                        <span className="text-slate-400 group-hover:text-slate-700">
                          →
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <InfoBox title="Important" tone="warn">
                These Terms apply to all users including students, tutors,
                institutions, and visitors. Please read them carefully.
              </InfoBox>
            </div>
          </div>

          {/* Sections */}
          <div className="grid gap-6">
            <Section id="definitions" title="1. Definitions">
              <RuleList
                items={[
                  "“Goat Teachers,” “we,” “us,” or “our” refers to Goat Teachers and its owners, operators, affiliates, and partners.",
                  "“User” refers to any individual or entity accessing or using the Platform, including students, tutors, institutions, and visitors.",
                  "“Student” refers to users seeking educational resources, tuition, reviews, or academic services.",
                  "“Tutor” refers to users offering tuition, coaching, or educational services.",
                  "“Content” includes text, reviews, ratings, images, files, notes, assignments, messages, and all other materials uploaded or generated on the Platform.",
                  "“Credits” refer to prepaid digital units used to unlock certain platform features.",
                ]}
              />
            </Section>

            <Section id="eligibility" title="2. Eligibility">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>
                  You must be at least 13 years of age to use Goat Teachers.
                </p>
                <p>
                  If you are under 18, you confirm that you have parental or
                  legal guardian consent.
                </p>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    By using the Platform, you represent and warrant that:
                  </div>
                  <div className="mt-2">
                    <RuleList
                      items={[
                        "You have the legal capacity to enter into these Terms.",
                        "All information you provide is accurate and truthful.",
                      ]}
                    />
                  </div>
                </div>
              </div>
            </Section>

            <Section id="overview" title="3. Platform Overview">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>
                  Goat Teachers operates as a multi-network student platform,
                  including but not limited to:
                </p>
                <RuleList
                  items={[
                    "Goat Teachers – University and faculty ratings and reviews",
                  ]}
                />
                <InfoBox title="Neutral Platform" tone="neutral">
                  Goat Teachers does not provide educational services directly
                  and is not a party to any agreement between students and
                  tutors.
                </InfoBox>
              </div>
            </Section>

            <Section id="accounts" title="4. User Accounts">
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="space-y-3">
                  <div className="text-sm font-extrabold text-slate-900">
                    4.1 Registration
                  </div>
                  <p className="text-sm leading-relaxed text-slate-700">
                    To access certain features, you may be required to create an
                    account. You agree to:
                  </p>
                  <RuleList
                    items={[
                      "Provide accurate and complete information",
                      "Maintain confidentiality of your login credentials",
                      "Accept responsibility for all activities under your account",
                    ]}
                  />
                </div>

                <div className="space-y-3">
                  <div className="text-sm font-extrabold text-slate-900">
                    4.2 Account Suspension or Termination
                  </div>
                  <p className="text-sm leading-relaxed text-slate-700">
                    We reserve the right to suspend, restrict, or terminate any
                    account at our sole discretion if:
                  </p>
                  <RuleList
                    items={[
                      "These Terms are violated",
                      "Fraudulent or abusive behavior is detected",
                      "Platform integrity or user safety is compromised",
                    ]}
                  />
                </div>
              </div>
            </Section>

            <Section id="tutor" title="5. Tutor Responsibilities">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>Tutors acknowledge and agree that:</p>
                <RuleList
                  items={[
                    "They are independent individuals, not employees or agents of Goat Teachers",
                    "All tuition agreements, payments, schedules, and outcomes are strictly between tutor and student",
                    "Goat Teachers does not guarantee student engagement, income, or results",
                  ]}
                />
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Tutors must not:
                  </div>
                  <div className="mt-2">
                    <RuleList
                      items={[
                        "Misrepresent qualifications",
                        "Harass or exploit students",
                        "Circumvent platform systems, including credit mechanisms",
                      ]}
                    />
                  </div>
                </div>
              </div>
            </Section>

            <Section id="student" title="6. Student Responsibilities">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>Students acknowledge that:</p>
                <RuleList
                  items={[
                    "Tutor selection is their sole responsibility",
                    "Goat Teachers does not verify tutor performance beyond available platform data",
                    "Academic outcomes are not guaranteed",
                  ]}
                />
              </div>
            </Section>

            <Section
              id="credits"
              title="7. Credits, Payments, and Refund Policy"
            >
              <div className="grid gap-6 lg:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    7.1 Credits
                  </div>
                  <div className="mt-2">
                    <RuleList
                      items={[
                        "Certain features require Credits",
                        "Credits are non-transferable",
                        "Credits have no cash value",
                      ]}
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    7.2 Payments
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">
                    Payments are processed via third-party providers (e.g.,
                    bKash). Goat Teachers does not store payment credentials.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    7.3 Refunds
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">
                    All purchases are final and non-refundable, unless required
                    by law or explicitly stated otherwise.
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <InfoBox title="Anti-circumvention" tone="danger">
                  Any attempt to bypass platform systems, including credit
                  mechanisms, may result in account restriction or suspension.
                </InfoBox>
              </div>
            </Section>

            <Section id="content" title="8. Reviews, Ratings, and User Content">
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="space-y-3">
                  <div className="text-sm font-extrabold text-slate-900">
                    8.1 Content Ownership
                  </div>
                  <p className="text-sm leading-relaxed text-slate-700">
                    Users retain ownership of their Content but grant Goat
                    Teachers a worldwide, royalty-free, perpetual license to
                    use, display, modify, and distribute such Content for
                    platform operations and promotion.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="text-sm font-extrabold text-slate-900">
                    8.2 Prohibited Content
                  </div>
                  <p className="text-sm leading-relaxed text-slate-700">
                    Users must not upload Content that is:
                  </p>
                  <RuleList
                    items={[
                      "False, misleading, or defamatory",
                      "Abusive, hateful, or harassing",
                      "Infringing on intellectual property rights",
                      "Illegal or unethical",
                    ]}
                  />
                  <InfoBox title="Removal" tone="neutral">
                    We reserve the right to remove any Content without notice.
                  </InfoBox>
                </div>
              </div>
            </Section>

            <Section id="ip" title="9. Intellectual Property">
              <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                <p>
                  All platform designs, trademarks, logos, software, and
                  proprietary systems are the exclusive property of Goat
                  Teachers.
                </p>
                <p>
                  Unauthorized copying, reverse engineering, or commercial use
                  is strictly prohibited.
                </p>
              </div>
            </Section>

            <Section id="privacy" title="10. Privacy">
              <div className="text-sm leading-relaxed text-slate-700">
                Your use of Goat Teachers is also governed by our Privacy
                Policy, which explains how we collect, store, and use personal
                data.
              </div>
            </Section>

            <Section id="disclaimers" title="11. Disclaimers">
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    11.1 No Guarantees
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">
                    Goat Teachers provides the Platform “as is” and “as
                    available.” We make no guarantees regarding:
                  </p>
                  <div className="mt-2">
                    <RuleList
                      items={[
                        "Tutor quality",
                        "Academic success",
                        "Employment or earnings",
                        "Availability or accuracy of content",
                      ]}
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    11.2 Third-Party Interactions
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">
                    All interactions between users occur at their own risk. Goat
                    Teachers is not responsible for disputes, damages, or losses
                    arising from user interactions.
                  </p>
                </div>
              </div>
            </Section>

            <Section id="liability" title="12. Limitation of Liability">
              <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                <p>
                  To the maximum extent permitted by law: Goat Teachers shall
                  not be liable for any:
                </p>
                <RuleList
                  items={[
                    "Indirect, incidental, or consequential damages",
                    "Loss of data, reputation, income, or opportunity",
                    "Academic or professional outcomes",
                  ]}
                />
                <InfoBox title="Liability Cap" tone="neutral">
                  Our total liability, if any, shall not exceed the amount paid
                  by the user to Goat Teachers in the preceding 3 months.
                </InfoBox>
              </div>
            </Section>

            <Section id="indemnification" title="13. Indemnification">
              <div className="text-sm leading-relaxed text-slate-700">
                You agree to indemnify and hold harmless Goat Teachers from any
                claims, damages, losses, liabilities, and expenses arising from:
                <div className="mt-2">
                  <RuleList
                    items={[
                      "Your use of the Platform",
                      "Your Content",
                      "Violation of these Terms",
                      "Disputes with other users",
                    ]}
                  />
                </div>
              </div>
            </Section>

            <Section id="termination" title="14. Termination">
              <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                <p>
                  We may terminate or suspend access to the Platform at any
                  time, without notice, for any reason, including violation of
                  these Terms.
                </p>
                <p>
                  All provisions intended to survive termination shall remain in
                  effect.
                </p>
              </div>
            </Section>

            <Section id="modifications" title="15. Modifications to Terms">
              <div className="text-sm leading-relaxed text-slate-700">
                Goat Teachers reserves the right to update these Terms at any
                time. Continued use of the Platform after changes constitutes
                acceptance.
              </div>
            </Section>

            <Section id="law" title="16. Governing Law and Jurisdiction">
              <div className="text-sm leading-relaxed text-slate-700">
                These Terms shall be governed by and construed in accordance
                with the laws of the People’s Republic of Bangladesh. Any
                disputes shall be subject to the exclusive jurisdiction of the
                courts of Bangladesh.
              </div>
            </Section>

            <Section id="severability" title="17. Severability">
              <div className="text-sm leading-relaxed text-slate-700">
                If any provision of these Terms is found unenforceable, the
                remaining provisions shall remain in full force and effect.
              </div>
            </Section>

            <Section id="contact" title="18. Contact Information">
              <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                <p>For legal or support inquiries, please contact:</p>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Goat Teachers
                  </div>
                  <div className="mt-2 space-y-1">
                    <div>
                      Email:{" "}
                      <a
                        href="mailto:support@goatteachers.com"
                        className="font-bold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-900"
                      >
                        support@goatteachers.com
                      </a>
                    </div>
                    <div>
                      Website:{" "}
                      <a
                        href="https://www.goatteachers.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-900"
                      >
                        https://www.goatteachers.com
                      </a>
                    </div>
                  </div>
                </div>

                <InfoBox title="Note" tone="neutral">
                  For faster resolution, please include your account email/phone
                  (if applicable), and a brief description of the issue.
                </InfoBox>
              </div>
            </Section>

            {/* Bottom CTA / footer note */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
              <div className="font-extrabold text-slate-900">Reminder</div>
              <p className="mt-1 leading-relaxed">
                By continuing to use Goat Teachers, you acknowledge that you
                have read and agree to these Terms &amp; Conditions.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default TermsAndConditions;
